import { useState, type FormEvent } from 'react';
import type { ClaimDirection, ClaimType } from '@entities/legal/types';
import {
  useCreateClaimMutation,
  useListContractsQuery,
  useUploadClaimMutation,
} from '@shared/api';
import { ACCEPT_LEGAL_DOC } from '@shared/lib/fileAccept';
import { formatFileCount } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import { MultiFileList } from '@shared/ui/MultiFileList';
import { CounterpartyPicker } from '../CounterpartyPicker';
import {
  ContractFieldDescription,
  DirectionOption,
  DirectionRadios,
  ModeTab,
  ModeTabs,
} from './styles';
import type { Mode } from './types';

interface CreateClaimModalProps {
  open: boolean;
  mode: Mode;
  canWrite: boolean;
  onClose: () => void;
  onModeChange: (mode: Mode) => void;
  onClaimCreated: (claimId?: string) => void;
}

export function CreateClaimModal({
  open,
  mode,
  canWrite,
  onClose,
  onModeChange,
  onClaimCreated,
}: CreateClaimModalProps) {
  const { data: contractsData } = useListContractsQuery();
  const [createClaim, { isLoading: isCreating }] = useCreateClaimMutation();
  const [uploadClaim, { isLoading: isUploading }] = useUploadClaimMutation();

  const [claimType, setClaimType] = useState<ClaimType>('claim');
  const [direction, setDirection] = useState<ClaimDirection>('incoming');
  const [counterparty, setCounterparty] = useState('');
  const [counterpartyId, setCounterpartyId] = useState<string | null>(null);
  const [disputeType, setDisputeType] = useState('');
  const [contractId, setContractId] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [amount, setAmount] = useState('');

  function resetForm() {
    setFiles([]);
    setClaimType('claim');
    setDirection('incoming');
    setCounterparty('');
    setCounterpartyId(null);
    setDisputeType('');
    setContractId('');
    setAmount('');
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  async function onSubmitManual(e: FormEvent) {
    e.preventDefault();
    if (!counterparty.trim()) return;
    await createClaim({
      claim_type: claimType,
      direction,
      counterparty,
      dispute_type: disputeType || undefined,
      amount: amount ? Number(amount) : undefined,
      contract_id: contractId || undefined,
    }).unwrap();
    resetForm();
    onClose();
    onClaimCreated();
  }

  async function onSubmitUpload(e: FormEvent) {
    e.preventDefault();
    if (files.length === 0 || !counterparty.trim()) return;
    const result = await uploadClaim({
      files,
      claim_type: claimType,
      direction,
      counterparty,
      counterparty_id: counterpartyId ?? undefined,
      dispute_type: disputeType || undefined,
      contract_id: contractId || undefined,
    }).unwrap();
    resetForm();
    onClose();
    onClaimCreated(result.claim_id);
  }

  return (
    <Modal
      open={open}
      title={mode === 'upload' ? 'Загрузить претензию / иск' : 'Новая запись вручную'}
      onClose={handleClose}
    >
      <ModeTabs>
        <ModeTab type="button" $active={mode === 'upload'} onClick={() => onModeChange('upload')}>
          Загрузить файл
        </ModeTab>
        <ModeTab type="button" $active={mode === 'manual'} onClick={() => onModeChange('manual')}>
          Только метаданные
        </ModeTab>
      </ModeTabs>

      {mode === 'upload' ? (
        <form onSubmit={onSubmitUpload}>
          <Field>
            <Label>Файлы претензии</Label>
            <FileDropZone
              accept={ACCEPT_LEGAL_DOC}
              multiple
              onFiles={(picked) =>
                setFiles((prev) => {
                  const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
                  const next = [...prev];
                  for (const f of picked) {
                    const key = `${f.name}::${f.size}`;
                    if (!seen.has(key)) {
                      next.push(f);
                      seen.add(key);
                    }
                  }
                  return next;
                })
              }
              hint="Претензия + расчёт + приложения. DOCX, PDF или DOC — до 25 МБ каждый"
            />
            <MultiFileList
              files={files}
              onRemove={(idx) => setFiles((prev) => prev.filter((_, i) => i !== idx))}
            />
          </Field>

          <Field>
            <Label>Направление</Label>
            <DirectionRadios>
              <DirectionOption $active={direction === 'incoming'}>
                <input
                  type="radio"
                  name="direction"
                  value="incoming"
                  checked={direction === 'incoming'}
                  onChange={() => setDirection('incoming')}
                />
                <strong>Входящая (от контрагента/суда)</strong>
                <span>
                  Защитный анализ: срок ответа, СИД, проверка против договора,
                  перерасчёт неустойки, контраргументы, проект ответа.
                </span>
              </DirectionOption>
              <DirectionOption $active={direction === 'outgoing'}>
                <input
                  type="radio"
                  name="direction"
                  value="outgoing"
                  checked={direction === 'outgoing'}
                  onChange={() => setDirection('outgoing')}
                />
                <strong>Исходящая (наш проект)</strong>
                <span>
                  Самопроверка: позиция, расчёт, СИД, доказательства, прогноз
                  ответа контрагента и контр-аргументы.
                </span>
              </DirectionOption>
            </DirectionRadios>
          </Field>

          <Field>
            <Label htmlFor="claim_type_u">Тип документа</Label>
            <Select
              id="claim_type_u"
              value={claimType}
              onChange={(e) => setClaimType(e.target.value as ClaimType)}
            >
              <option value="claim">Претензия</option>
              <option value="lawsuit">Иск</option>
            </Select>
          </Field>
          <CounterpartyPicker
            counterpartyId={counterpartyId}
            counterpartyName={counterparty}
            onChange={({ id, name }) => {
              setCounterpartyId(id);
              setCounterparty(name);
            }}
          />
          <Field>
            <Label htmlFor="dispute_u">Предмет спора (опционально)</Label>
            <Input
              id="dispute_u"
              value={disputeType}
              onChange={(e) => setDisputeType(e.target.value)}
              placeholder="LLM уточнит при анализе"
            />
          </Field>
          <Field>
            <Label htmlFor="contract_u">Связанный договор</Label>
            <Select
              id="contract_u"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
            >
              <option value="">— не связан —</option>
              {(contractsData?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.counterparty}
                  {c.subject ? ` — ${c.subject}` : ''}
                </option>
              ))}
            </Select>
            <ContractFieldDescription>
              Для входящих сильно повышает качество анализа — модель сверит
              ставки неустойки и сроки с условиями договора.
            </ContractFieldDescription>
          </Field>
          <Button
            type="submit"
            disabled={isUploading || files.length === 0 || !counterparty.trim() || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {isUploading
              ? 'Загружаем…'
              : files.length > 1
                ? `Загрузить ${formatFileCount(files.length)} и запустить анализ`
                : 'Загрузить и запустить анализ'}
          </Button>
        </form>
      ) : (
        <form onSubmit={onSubmitManual}>
          <Field>
            <Label>Направление</Label>
            <DirectionRadios>
              <DirectionOption $active={direction === 'incoming'}>
                <input
                  type="radio"
                  name="direction_m"
                  value="incoming"
                  checked={direction === 'incoming'}
                  onChange={() => setDirection('incoming')}
                />
                <strong>Входящая</strong>
                <span>От контрагента/суда</span>
              </DirectionOption>
              <DirectionOption $active={direction === 'outgoing'}>
                <input
                  type="radio"
                  name="direction_m"
                  value="outgoing"
                  checked={direction === 'outgoing'}
                  onChange={() => setDirection('outgoing')}
                />
                <strong>Исходящая</strong>
                <span>Наш проект</span>
              </DirectionOption>
            </DirectionRadios>
          </Field>
          <Field>
            <Label htmlFor="claim_type">Тип</Label>
            <Select
              id="claim_type"
              value={claimType}
              onChange={(e) => setClaimType(e.target.value as ClaimType)}
            >
              <option value="claim">Претензия</option>
              <option value="lawsuit">Иск</option>
            </Select>
          </Field>
          <CounterpartyPicker
            counterpartyId={counterpartyId}
            counterpartyName={counterparty}
            onChange={({ id, name }) => {
              setCounterpartyId(id);
              setCounterparty(name);
            }}
          />
          <Field>
            <Label htmlFor="dispute">Предмет спора</Label>
            <Input
              id="dispute"
              value={disputeType}
              onChange={(e) => setDisputeType(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="amount">Сумма требований</Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="contract_id">Связанный договор</Label>
            <Select
              id="contract_id"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
            >
              <option value="">— не связан —</option>
              {(contractsData?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.counterparty}
                  {c.subject ? ` — ${c.subject}` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            type="submit"
            disabled={isCreating || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {isCreating ? 'Создаём…' : 'Создать без файла'}
          </Button>
        </form>
      )}
    </Modal>
  );
}
